package ui.app.assign.detail.wheel.preview

import AppBuildConfig
import androidx.compose.runtime.*
import androidx.compose.ui.graphics.ImageBitmap
import api.ResourceType
import common.frontend.api.Io
import common.frontend.api.canLayerBottomDelete
import common.frontend.api.canLayerBottomEdit
import io.Repository
import io.ktor.utils.io.core.*
import kotlinx.coroutines.launch
import org.ssoft.common.tools.nowDateTime
import org.ssoft.common.ui.wheel.AppWheelFactory
import tk.mallumo.compose.navigation.viewmodel.NavigationViewModel
import tk.mallumo.compose.navigation.viewmodel.VM
import tk.mallumo.log.logERROR
import tk.mallumo.utils.DataState
import tk.mallumo.utils.mutableDataState
import tools.formatFromResAwait
import ui.snackBarStateGlobal
import kotlin.time.Clock

@VM
class AppPreviewWheelVM : NavigationViewModel() {

    private lateinit var scopeUi: AppPreviewWheelScope

    var state by mutableDataState<AppPreviewWheelScope.Wrapper>()
        private set

    var stateHistory by mutableDataState<Io.WheelHistoryOverview>()
        private set

    private val _showDownloadButton = mutableStateOf(false)
    val showDownloadButton: State<Boolean> get() = _showDownloadButton


    var wheelImage by mutableStateOf<ImageBitmap?>(null)
        private set

    private val wheelGenerator by lazy {
        AppWheelFactory.createWheelGenerator(
            loadSvg = {
                runCatching {
                    Repository.files.fetchIcon(it)
                }.getOrNull()
            },
            loadWebp = {
                runCatching {
                    Repository.files.fetchImage(it)
                }.getOrNull()
            }
        )
    }

    sealed interface Action {

        data class Archive(val doArchive: Boolean) : Action
        data class Delete(val withLogo: Boolean, val withContent: Boolean) : Action
        data object ExportHistory : Action

        companion object {
            @Composable
            fun rememberAction(vm: AppPreviewWheelVM): (Action) -> Unit {
                return remember {
                    { act ->
                        when (act) {
                            is Delete -> vm.delete(act.withLogo, act.withContent)
                            is Archive -> vm.archive(act.doArchive)
                            ExportHistory -> vm.exportHistory()
                        }
                    }
                }
            }
        }
    }

    private fun archive(archive: Boolean) {
        scope.launch {
            runCatching {
                Repository.api.app.archive(
                    companyId = scopeUi.args.companyId,
                    appId = scopeUi.args.appId,
                    doArchive = archive
                )
            }.onFailure {
                snackBarStateGlobal.showSnackbar(
                    message = it.message ?: "Archivation state change failure",
                    withDismissAction = true
                )
                it.printStackTrace()
            }.onSuccess {
                scopeUi.nav.up()
            }
        }
    }

    private fun regeneratePreview(wrapper: AppPreviewWheelScope.Wrapper) {
        wheelGenerator.postTask(wrapper.appRef.content)
    }


    private fun reloadHistory() {
        scope.launch {
            stateHistory = DataState.Loading()
            runCatching {
                DataState.Result(Repository.api.game.wheelHistory(scopeUi.args.companyId, scopeUi.args.appId))
            }.onFailure {
                logERROR(it)
                stateHistory = DataState.Error("Fail to load", throwable = it)
            }.onSuccess {
                stateHistory = it
                _showDownloadButton.value = !it.entry?.items.isNullOrEmpty()
            }
        }
    }

    fun init(scopeUi: AppPreviewWheelScope) {
        this.scopeUi = scopeUi
        state = DataState.Loading()
        reloadHistory()
        scope.launch {
            runCatching {
                val app = Repository.api.app.single(scopeUi.args.companyId, scopeUi.args.appId)
                state = DataState.Result(
                    AppPreviewWheelScope.Wrapper(
                        canEdit = scopeUi.login.canLayerBottomEdit(scopeUi.args.companyId),
                        canDelete = scopeUi.login.canLayerBottomDelete(scopeUi.args.companyId),
                        data = app
                    ) { nnvl ->
                        regeneratePreview(nnvl)
                    }
                )
            }.onFailure {
                it.printStackTrace()
                state = DataState.Error("Connection fail ${it.message}")
            }.onSuccess {
                wheelGenerator.launchCollector(scopeUi.textResolver) {
                    wheelImage = it
                }
            }
        }

    }

    private fun delete(withLogo: Boolean, withContent: Boolean) {
        scope.launch {
            state.entry?.data?.apply {
                runCatching {
                    Repository.api.app.delete(scopeUi.args.companyId, id, withLogo, withContent)
                }.onSuccess {
                    scopeUi.nav.up()
                }.onFailure {
                    snackBarStateGlobal.showSnackbar(
                        it.message ?: "Fail to delete",
                        withDismissAction = true
                    )
                    it.printStackTrace()
                }
            }

        }
    }

    override fun onRelease() {
        state = DataState.Idle()
    }

    fun exportHistory() {

        fun String.toCsvField() = "\"${replace("\"", "”")}\""

        scope.launch {
            tools.FilePicker.uploadBinary(
                buildString {
                    appendLine("CODE,DATE,NAME,ICON")
                    stateHistory.entry?.items?.forEach {

                        val iconLink = when {
                            it.item.icon.svg != ResourceType.NO_ICON -> {
                                "${AppBuildConfig.URL_ICONS}/api/icon/${it.item.icon.svg}/svg".toCsvField()
                            }

                            it.item.icon.webp != ResourceType.NO_IMAGE -> {
                                "${AppBuildConfig.HOST_FILES}/api/image/thumbnail/${it.item.icon.webp}".toCsvField()
                            }

                            else -> "-".toCsvField()
                        }
                        append(it.code.toCsvField())
                        append(",")
                        append(it.dt.toString().toCsvField())
                        append(",")
                        append(it.item.name.toCsvField())
                        append(",")
                        append(iconLink)
                        appendLine()
                    }
                }.toByteArray(),
                "text/csv",
                "${state.entry?.name}_export_${Clock.nowDateTime().formatFromResAwait()}.csv"
            )
        }
    }


}
